import type { SchoolCode } from '../types';
import {
  formatGradeDisplay,
  getGrade,
  getGradeFromMarks,
  getRemark,
} from './msceGrading';

const LETTER_SCALE: { letter: string; min: number; max: number; remark: string }[] = [
  { letter: 'A*', min: 90, max: 100, remark: 'Excellent' },
  { letter: 'A', min: 80, max: 89, remark: 'Very Good' },
  { letter: 'B', min: 70, max: 79, remark: 'Good' },
  { letter: 'C', min: 60, max: 69, remark: 'Satisfactory' },
  { letter: 'D', min: 50, max: 59, remark: 'Pass' },
  { letter: 'E', min: 40, max: 49, remark: 'Pass' },
  { letter: 'F', min: 0, max: 39, remark: 'Fail' },
];

export function usesLetterGrading(school: string, form: number | null | undefined): boolean {
  if (school === 'TLC') return true;
  if (school === 'KBA' && form != null && form >= 1 && form <= 2) return true;
  return false;
}

export function letterFromMarks(marks: number): string {
  const m = Math.max(0, Math.min(100, marks));
  for (const row of LETTER_SCALE) {
    if (m >= row.min && m <= row.max) return row.letter;
  }
  return 'F';
}

export function remarkFromLetter(letter: string): string {
  const row = LETTER_SCALE.find((r) => r.letter === letter);
  return row?.remark ?? 'Fail';
}

export function formatLetterGradeDisplay(letter: string): string {
  return `${letter} — ${remarkFromLetter(letter)}`;
}

/** Distinct colors for letter badges (not MSCE). */
export function getLetterGradeColor(letter: string): string {
  if (letter === 'A*' || letter === 'A') return '#28a745';
  if (letter === 'B' || letter === 'C') return '#17a2b8';
  if (letter === 'D' || letter === 'E') return '#ffc107';
  return '#dc3545';
}

export function getGradeColorForContext(
  school: SchoolCode,
  form: number | null | undefined,
  marks: number
): string {
  if (usesLetterGrading(school, form)) {
    return getLetterGradeColor(letterFromMarks(marks));
  }
  const { grade } = getGradeFromMarks(marks);
  if (grade <= 2) return '#28a745';
  if (grade <= 6) return '#17a2b8';
  if (grade <= 8) return '#ffc107';
  return '#dc3545';
}

/**
 * Display string for a grade row: letter mode uses letter + remark; MSCE uses points.
 */
/** Short code stored in API: letter or MSCE digit string. */
export function gradeCodeFromMarks(
  school: SchoolCode,
  form: number | null | undefined,
  marks: number
): string {
  if (usesLetterGrading(school, form)) {
    return letterFromMarks(marks);
  }
  return String(getGrade(marks));
}

export function formatGradeForContext(
  school: SchoolCode,
  form: number | null | undefined,
  marks: number,
  storedGrade?: string | null
): string {
  if (usesLetterGrading(school, form)) {
    const letter =
      storedGrade && /^(A\*|[A-F])$/.test(storedGrade)
        ? storedGrade
        : letterFromMarks(marks);
    return formatLetterGradeDisplay(letter);
  }
  const n = storedGrade != null ? parseInt(storedGrade, 10) : NaN;
  if (!Number.isNaN(n) && n >= 1 && n <= 9) {
    return formatGradeDisplay(n, getRemark(n));
  }
  const { grade, remark } = getGradeFromMarks(marks);
  return formatGradeDisplay(grade, remark);
}
